import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { getInheritedSettings, getSourceAndLabel } from '../PositionRateSettingsForm.utils';
import { useProRateEffect } from './useProRateEffect';
import { useChargeRateEffect } from './useChargeRateEffect';
import { useMarginEffect } from './useMarginEffect';
import { useScalingMarginEffect } from './useScalingMarginEffect';

type UseRateCalculationsProps = {
  source: string;
  inheritedSettings: any;
};

export const useRateCalculations = ({ source, inheritedSettings }: UseRateCalculationsProps) => {
  const { watch, setValue, setError, clearErrors } = useFormContext();

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const { field } = getSourceAndLabel(source);
  const isW2Local = source.includes('w2.local');

  const applicableField = isW2Local ? 'base_margin_percent_w2' : field;

  const chargeRateCents = watch(`${source}.charge_rate_cents`);
  const proRateCents = watch(`${source}.pro_rate_cents`);
  const blendedRateCents = watch(`${source}.blended_rate_cents`);
  const blendedRateMarkup = watch(`${source}.blended_rates.blended_rate_markup`);
  const useBlendedRate = watch(`${source}.use_blended_rate`);
  const marginPercent = watch(`${source}.${applicableField}`);
  const lockedRateType = watch(`${source}.lockedRateType`) || 'lock_charge_rate';

  const watchedFields = useMemo(
    () => ({
      chargeRateCents,
      proRateCents,
      blendedRateCents,
      blendedRateMarkup,
      useBlendedRate,
      marginPercent,
      lockedRateType,
    }),
    [
      chargeRateCents,
      proRateCents,
      blendedRateCents,
      blendedRateMarkup,
      useBlendedRate,
      marginPercent,
      lockedRateType,
    ],
  );

  const { validateBaseMarginPercentW2 } = useProRateEffect({
    isW2Local,
    setError,
    clearErrors,
    setValue,
    source,
  });

  const {
    validateMarginPercentGuardrail,
    validateIncrementalMargin,
    validateMarginAgainstGuardrail,
  } = useScalingMarginEffect({
    setError,
    clearErrors,
    watch,
    setValue,
    source,
    applicableField,
    isW2Local,
  });

  const { handleChargeRateChange, handleBlendedRateChange, handleProfessionalRateChange } =
    useChargeRateEffect({
      source,
      inheritedSettings,
      watchedFields,
      mergedSettings,
      setValue,
      watch,
      isW2Local,
      validateBaseMarginPercentW2,
    });

  const { handleMarginChange, handleLockTypeChange, handleRateStructureChange } = useMarginEffect({
    source,
    inheritedSettings,
    watchedFields,
    setValue,
    watch,
    isW2Local,
    validateBaseMarginPercentW2,
    validateMarginAgainstGuardrail,
    blendedRateMarkup,
  });

  return {
    watchedFields,
    mergedSettings,
    handlers: {
      handleChargeRateChange,
      handleBlendedRateChange,
      handleProfessionalRateChange,
      handleMarginChange,
      handleLockTypeChange,
      handleRateStructureChange,
    },
    validations: {
      validateMarginPercentGuardrail,
      validateIncrementalMargin,
    },
  };
};
